import { action, computed, observable, runInAction, makeObservable } from 'mobx';

import {priorities} from '../../models'
import {Task, WorkflowTaskStore} from '../WorkflowTaskStore';


export const permissionsForTask = [
  'EmailAlertManagement',
  'UserManagement',
  'TaskAdmin',
  'AccountOwner',
  'ManageExternalAccounts',
  'AddExternalAccounts',
  'EditScoreConfig'
];

export const permissionToLabels = {
  'EmailAlertManagement': ['Set Up Email Alerts'],
  'UserManagement': ['Add Users'],
  'TaskAdmin': ['Configure Workflow Settings'],
  'AccountOwner': ['Set Up Pulse Topics'],
  'ManageExternalAccounts': ['Add External Logins'],
  'AddExternalAccounts': ['Add Social Accounts'],
  'EditScoreConfig': ['Configure LBV Score Settings']
};


export const labelsToDescriptions = {
  'Set Up Email Alerts':
  `Set up email alerts for the information you'd like to receive daily.

To get started, follow these steps:
1. Click on My Alerts in your top menu bar.
2. Click the Add New Alerts button.
3. Select a Template to use.
4. Configure the Alert Settings.
5. Select the contents to include in your alert by adding Gadgets.`,


  'Add Users':
  `Make sure everyone in your organization has access to the dashboard.

To get started, follow these steps:
1. Click on My Account from your top menu bar.
2. Click on Manage Users in the left-hand navigation.
3. Click on the Add New User button.
4. Complete the user information form, including their User Type.
5. For Standard Users, after saving their profile click on Edit Groups or Edit Locations from the Manage Users home to configure their location access within the dashboard.`,


  'Configure Workflow Settings':
    `Set up rules for what system tasks you'd like automatically created and how they're assigned.
    
To get started, follow these steps:
1. Go to the Workflow Tab.
2. Click on Settings in your menu on the left.
3. Select the type of task you're interested in (Reviews, Listings, etc.)
4. Open any of the default rules or click the Add Rule button to start a new one.
5. Select your desired filters for the task and the settings that should be applied when new tasks are created and assigned.`,



  'Set Up Pulse Topics':
    `Configure the topics you'd like to track sentiment for on Pulse.
    
To get started, follow these steps:
1. Go to the Pulse tab.
2. Your current Tracked Topics will be listed at the top of the page. On the right-hand side, click on the Edit Topic Set to make edits to this list or the Add New Topic Set to start a new one.
3. In the Topics bar, click inside of it to enter a new topic. If you'd like to edit or delete existing topics, click on the buttons associated with each.`,



  'Add External Logins':
  `Link your user credentials on third party sites (like Google and Yelp) to the dashboard for review responding and reporting analytics.

To get started, follow these steps:
1. Click on My Account from your top menu bar.
2. Click on External Logins in the left-hand navigation.
3. Click on the Add Login button and select the site you'd like to add user credentials for.
4. Follow the instructions to link your account to the dashboard.`,


  'Add Social Accounts':
  `Link your user credentials on social media sites (like Facebook and Twitter) to the dashboard for social media monitoring and publishing.

To get started, follow these steps:
1. Go to the Social tab.
2. Click on Settings in your menu on the left.
3. Select the site you'd like to add your social account for.
4. Click on the Login button to login and link your account to the dashboard.`,

  'Configure LBV Score Settings':
  `Apply the custom scoring criteria you'd like your LBV Score to be based on.

To get started, follow these steps:
1. Click on My Account from your top menu bar.
2. Click on Manage Scoring in the left-hand navigation.
3. Edit the current score settings by expanding each scoring category and sliding the weights of metrics or providers to your desired levels.
4. If you'd like to establish different settings for different industries/accounts, you can do so by clicking on the Create Template button.`,

};

export class GenerateTaskStore {
  defaults = {'Set Up Email Alerts': false, 'Add Users': false, 'Configure Workflow Settings': false, 'Set Up Pulse Topics': false, 'Add External Logins': false, 'Add Social Accounts': false, 'Configure LBV Score Settings': false}
  tasks = {...this.defaults};

  priority = {...priorities[2]};
  dueDate;
  isLoading = false;

  onToggle = (task, state) => {
    console.log(this.tasks);
    this.tasks[task] = state;
  };

  setPriority = (priority) => {
    this.priority = priority;
  };

  setDueDate = (date) => {
    this.dueDate = date;
  };

  generateTasks = (user) => {
    this.isLoading = true;
    return Promise.all(Object.keys(this.tasks)
      .map(key => {
        if(this.tasks[key]){
          const t = Task({
            taskName: key,
            assignee: {userId: user.id, ...user},
            description: labelsToDescriptions[key]
          });
          t.content.priority = this.priority.nameValue;
          t.content.dueDate = this.dueDate;
          return t;
        }
      })
      .filter(task => task)
      .map(task => {
        const store = new WorkflowTaskStore({task});
        return store.saveTask()
      })
    ).then(resp => {
      runInAction(() => {
        Object.keys(this.tasks).forEach(key => this.tasks[key] = false);
        this.isLoading = false;
      });
      return resp;
    });
  };

  reset = () => {
    this.tasks = {...this.defaults};
  };

  constructor() {
    makeObservable(this, {
      tasks: observable,
      priority: observable,
      dueDate: observable,
      isLoading: observable,
      onToggle: action,
      setPriority: action,
      setDueDate: action,
      generateTasks: action,
      reset: action,
      hasError: computed
    });
  }

  get hasError() {
    const keys = Object.keys(this.tasks);
    return !keys.map(key => this.tasks[key]).filter(bool => bool).length;
  }
}
